import { yupResolver } from '@hookform/resolvers/yup'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import type { FormData } from '@/pages/getRecipe/shadeGuide/utils/formTypes'
import {
  FORM_FIELDS,
  validationSchema,
} from '@/pages/getRecipe/shadeGuide/utils/formTypes'
import type { RootState } from '@/store'
import { useGenerateRecipeMutation } from '@/store/apiService'
import {
  setRestorationMethodOnRecipeGenerate,
  setShadeGuideActiveStep,
  setShadeGuideRecipeOutput,
} from '@/store/shadeGuide'
import {
  buildAnalogPayload,
  getAnalogRecipePath,
} from '@/utils/matisseV2/analog'

import { RECIPE_TYPE } from '../utils/constants'
import Material from './Material'
import MeasureColor from './MeasureColor'

const ShadeGuidePanel = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'buttons',
  })
  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'validation',
  })

  const navigate = useNavigate()
  const location = useLocation()
  const formValues = useSelector((state: RootState) => state.shadeGuide.form)
  const shadeGuideActiveStep = useSelector(
    (state: RootState) => state.shadeGuide.shadeGuideActiveStep,
  )

  const canvasImage = useSelector((state: RootState) => state.app.canvasImage)
  const currentCaseId = new URLSearchParams(location.search).get('caseId')

  const dispatch = useDispatch()

  const [generateRecipe] = useGenerateRecipeMutation()

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
    setValue,
    clearErrors,
  } = useForm<FormData>({
    defaultValues: formValues || {},
    resolver: yupResolver(validationSchema()),
    mode: 'onChange',
  })

  const recipeTypeValue = watch(FORM_FIELDS.RECIPE_TYPE) || ''
  const restorationMethodValue = watch(FORM_FIELDS.RESTORATION_METHOD) || ''
  const onNext = useCallback(async () => {
    if (!canvasImage?.image) {
      toast.error(translate('selectImage'))
      return
    }

    let isValid = false
    switch (shadeGuideActiveStep) {
      case 0: {
        const colorFieldsToValidate: (keyof FormData)[] = [
          FORM_FIELDS.RECIPE_TYPE,
          FORM_FIELDS.RESTORATION_METHOD,
          FORM_FIELDS.STUMP_SHADE,
          FORM_FIELDS.TARGET_COLOR,
          FORM_FIELDS.TARGET_COLOR_CHROMA_MIDDLE,
          FORM_FIELDS.TARGET_COLOR_VALUE_MIDDLE,
        ]

        if (recipeTypeValue === RECIPE_TYPE.TRIPLE_ZONE) {
          colorFieldsToValidate.push(
            FORM_FIELDS.TARGET_COLOR_CHROMA_CERVICAL,
            FORM_FIELDS.TARGET_COLOR_CHROMA_INCISAL,
            FORM_FIELDS.TARGET_COLOR_VALUE_CERVICAL,
            FORM_FIELDS.TARGET_COLOR_VALUE_INCISAL,
          )
        }

        isValid = await trigger(colorFieldsToValidate)
        break
      }
      case 1: {
        const fieldsToValidate: (keyof FormData)[] = [
          FORM_FIELDS.BRAND,
          FORM_FIELDS.FRAMEWORK_COLOR,
          FORM_FIELDS.FRAMEWORK_OPTIONS,
          FORM_FIELDS.STAINING_BRAND,
          FORM_FIELDS.AVAILABLE_SPACE_MIDDLE,
        ]

        if (recipeTypeValue === RECIPE_TYPE.TRIPLE_ZONE) {
          fieldsToValidate.push(
            FORM_FIELDS.AVAILABLE_SPACE_CERVICAL,
            FORM_FIELDS.AVAILABLE_SPACE_INCISAL,
          )
        }

        isValid = await trigger(fieldsToValidate)
        break
      }
    }

    if (isValid) {
      dispatch(setShadeGuideActiveStep(shadeGuideActiveStep + 1))
    }
  }, [
    dispatch,
    shadeGuideActiveStep,
    trigger,
    watch,
    canvasImage,
    recipeTypeValue,
    translate,
  ])

  const onBack = useCallback(() => {
    dispatch(setShadeGuideActiveStep(shadeGuideActiveStep - 1))
  }, [dispatch, shadeGuideActiveStep])

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!canvasImage?.image) {
        toast.error(translate('selectImage'))
        return
      }
      try {
        const restoration_method = data[FORM_FIELDS.RESTORATION_METHOD]
        const formData = data as FormData & Record<string, string>
        dispatch(setRestorationMethodOnRecipeGenerate(restoration_method))

        const payload = buildAnalogPayload({
          restorationMethod: restoration_method,
          recipeType: data[FORM_FIELDS.RECIPE_TYPE],
          naturalDieMaterial: data[FORM_FIELDS.STUMP_SHADE],
          stainingbrand: data[FORM_FIELDS.STAINING_BRAND],
          ceramicbrand: data[FORM_FIELDS.BRAND],
          frameworkColor: data[FORM_FIELDS.FRAMEWORK_COLOR],
          frameworkThickness: data[FORM_FIELDS.FRAMEWORK_OPTIONS],
          targetColors: {
            middle: [
              data[FORM_FIELDS.TARGET_COLOR_VALUE_MIDDLE],
              data[FORM_FIELDS.TARGET_COLOR_CHROMA_MIDDLE],
            ],
            cervical: [
              data[FORM_FIELDS.TARGET_COLOR_VALUE_CERVICAL],
              data[FORM_FIELDS.TARGET_COLOR_CHROMA_CERVICAL],
            ],
            incisal: [
              data[FORM_FIELDS.TARGET_COLOR_VALUE_INCISAL],
              data[FORM_FIELDS.TARGET_COLOR_CHROMA_INCISAL],
            ],
          },
          prepColors: {
            middle: formData.prepcolor_middle_value,
            cervical: formData.prepcolor_cervical_value,
            incisal: formData.prepcolor_incisal_value,
          },
          availspace: {
            middle: data[FORM_FIELDS.AVAILABLE_SPACE_MIDDLE],
            cervical: data[FORM_FIELDS.AVAILABLE_SPACE_CERVICAL],
            incisal: data[FORM_FIELDS.AVAILABLE_SPACE_INCISAL],
          },
        })

        generateRecipe({
          caseId: currentCaseId || '',
          recipePath: getAnalogRecipePath(restoration_method),
          data: payload,
        })
          .unwrap()
          .then((res) => {
            const { generation_id, ...output } = res ?? {}
            dispatch(
              setShadeGuideRecipeOutput({
                result: output,
                generation_id,
              }),
            )
            dispatch(setShadeGuideActiveStep(0))
            navigate(`/shade-guide-ai-output${location.search}`)
          })
          .catch((e) => {
            toast.error(e?.data?.message || 'Error generating recipe')
          })
      } catch (error) {
        toast.error(error?.data?.message || 'Error generating recipe')
        console.error('Form submission error:', error)
      }
    },
    [
      canvasImage,
      translate,
      currentCaseId,
      location.search,
      navigate,
      dispatch,
      generateRecipe,
    ],
  )

  const renderStepContent = useMemo(() => {
    switch (shadeGuideActiveStep) {
      case 0:
        return (
          <MeasureColor
            control={control}
            setValue={setValue}
            clearErrors={clearErrors}
          />
        )
      case 1:
        return (
          <Material
            control={control}
            recipeTypeValue={recipeTypeValue}
            restorationMethodValue={restorationMethodValue}
          />
        )

      default:
        return null
    }
  }, [shadeGuideActiveStep, control, recipeTypeValue, errors])

  const renderButtons = useMemo(
    () => (
      <Box px={2} py={5} display="flex" gap={2}>
        {shadeGuideActiveStep > 0 && (
          <Button
            label={t('back')}
            variant="outlined"
            onClick={onBack}
            startIcon={<ChevronLeftIcon />}
            textTransform="uppercase"
            sx={shadeGuideActiveStep === 1 ? {} : { flexGrow: 1 }}
          />
        )}
        {shadeGuideActiveStep < 1 ? (
          <Button
            label={t('next')}
            onClick={onNext}
            endIcon={<ChevronRightIcon />}
            textTransform="uppercase"
            sx={{ flexGrow: 1 }}
          />
        ) : (
          <Button
            label={t('generateRecipe')}
            onClick={handleSubmit(onSubmit)}
            textTransform="uppercase"
            sx={{ flexGrow: 2 }}
          />
        )}
      </Box>
    ),
    [shadeGuideActiveStep, onBack, onNext, handleSubmit, onSubmit],
  )

  return (
    <Box display="flex" flexDirection="column" flexGrow={1} overflow="auto">
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        gap={1}
        overflow="auto"
      >
        {renderStepContent}
      </Box>
      {renderButtons}
    </Box>
  )
}

export default ShadeGuidePanel
